/**
 * The participant's phone: the company's own app, the way a near-future firm would ship it. Scanning the code is
 * being hired (a short onboarding, then the employee badge). A ballot is a decision moment, not a form: the
 * phone announces it, asks one question at a time, shows what you chose, and you seal it by holding your thumb
 * on the seal; the decision leaves the phone for the stage and a receipt stays, with how many of your colleagues
 * have sealed theirs.
 *
 * Underneath, the phone is a lamp in a dark hall (`Mood`): a light layer and an edge glow behind dark glass,
 * in the group's colour or the scene's, pulsing in time with every other phone (server clock, `light.ts`).
 * Bright while a ballot needs you, a flash when you seal, dark once you have, so the hall goes dark as it decides.
 */
import type { BallotField, Mood, PhoneView } from '../src/platform/types.ts';
import type { ServerMsg } from '../src/server/protocol.ts';
import { connect, store } from './ws.ts';
import { renderComponents, setClock, setRenderErrorHandler, serverNow, h } from './render.ts';
import { lampAt, MOVING } from './light.ts';

const TOKEN_KEY = 'meridian-token';
const params = new URLSearchParams(location.search);
const preview = params.has('preview');
const root = document.getElementById('app') as HTMLElement;
document.body.classList.add('phone');
document.body.dataset.mood = 'dark';
if (preview) document.body.classList.add('is-preview');

root.innerHTML = '';
const layer = (cls: string) => h('div', cls, undefined, { 'aria-hidden': 'true' });
const lamp = layer('p-lamp');
const aura = layer('p-aura');
const grid = layer('p-gridbg');
const header = h('header', 'p-head');
const prog = layer('p-prog');
const comps = h('main', 'p-comps');
const ballotBox = h('section', 'p-ballot');
const dock = h('div', 'p-dock');
const cover = h('div', 'p-cover');
const hire = h('div', 'p-hire');
const intro = h('div', 'p-intro');
const fx = layer('p-fx');
const toast = h('div', 'p-toast', undefined, { role: 'status' });
const conn = h('div', 'conn');
root.append(lamp, aura, grid, header, comps, ballotBox, dock, cover, hire, intro, fx, toast, conn);

let token = preview ? undefined : (store(TOKEN_KEY) ?? undefined);
let view: PhoneView | null = null;
let online = false;
let shownScene = -2;

/* ballot state: the client walks the fields one at a time, then a review with the seal */
let selection: Record<string, string> = {};
let selBallot = '';
let ballotSeen = 0;
let saved = false;
let dirty = false;
let sending = false;
let step = 0;
let editing = false;
let sealedAt = 0;
let turnout: Extract<ServerMsg, { t: 'turnout' }> | null = null;
let hadBallot: { id: string; sealed: boolean } | null = null;

const client = connect({
  role: 'phone',
  preview,
  dept: params.get('dept') ?? undefined,
  token: () => token,
  onState: (ok) => { online = ok; conn.textContent = ok ? '' : 'Bağlantı yeniden kuruluyor…'; conn.classList.toggle('show', !ok); drawHead(); },
  onMessage: (m: ServerMsg) => {
    if (m.t === 'welcome') {
      if (m.token && m.token !== token) { selBallot = ''; selection = {}; saved = false; dirty = false; }
      if (m.token) { token = m.token; if (!preview) store(TOKEN_KEY, m.token); }
    } else if (m.t === 'phone') {
      view = m.view;
      try { draw(); } catch (e) { client.report(`phone draw: ${(e as Error).message}`); }
      client.seen(m.rev);
    } else if (m.t === 'turnout') {
      turnout = m;
      drawTurnout();
    } else if (m.t === 'ack') {
      if (m.ref === 'vote') { saved = true; dirty = false; sending = false; editing = false; sealedAt = Date.now(); launch(); drawBallot(); }
    } else if (m.t === 'error') {
      sending = false;
      conn.textContent = m.message; conn.classList.add('show');
      setTimeout(() => conn.classList.toggle('show', !online), 4000);
      drawBallot();
    }
  },
});
setClock(client.now);
setRenderErrorHandler((msg) => client.report(`phone ${msg}`));

// browsers refuse vibration before the first touch (and say so in the console), so ask only after one
const buzz = (p: number | number[]) => { const ua = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } }).userActivation; if (!preview && (!ua || ua.hasBeenActive)) navigator.vibrate?.(p); };

function drawHead(): void {
  if (!view) return;
  document.body.style.setProperty('--dept', view.dept.color);
  const brand = h('span', 'p-brand');
  brand.append(h('span', 'p-brand-mark', '◆'), h('span', 'p-brand-t', 'MERIDIAN'));
  const chip = h('span', 'p-chip');
  chip.append(h('span', 'p-dot'), h('span', 'p-dept', view.dept.name));
  const sig = h('span', 'p-signal ' + (online ? 'on' : 'off'), undefined, { title: online ? 'bağlı' : 'bağlantı yok' });
  for (let i = 0; i < 4; i++) sig.appendChild(h('i'));
  header.replaceChildren(brand, chip, preview ? h('span', 'p-badge', 'önizleme') : h('span'), h('span', 'p-id', view.memberId), sig, prog);
}

/** The show's progress as a row of segments under the header: one per scene, the current one lit. */
function drawProgress(v: PhoneView): void {
  const sc = v.scene; if (!sc) { prog.replaceChildren(); return; }
  if (prog.childElementCount !== sc.count) prog.replaceChildren(...Array.from({ length: sc.count }, () => h('i')));
  [...prog.children].forEach((e, i) => { e.className = i < sc.index ? 'past' : i === sc.index ? 'now' : ''; });
}

function draw(): void {
  if (!view) return;
  const v = view;
  document.body.dataset.mood = v.mood;
  document.body.dataset.tint = v.tint ?? '';
  document.body.dataset.turn = v.turn ?? '';
  drawHead();
  drawProgress(v);
  renderComponents(comps, v.components);
  // a new scene arrives like a transmission: the panels scan in one after another
  const idx = v.scene?.index ?? 0;
  if (idx !== shownScene) {
    if (shownScene !== -2) {
      comps.classList.remove('incoming'); aura.classList.remove('ripple'); void comps.offsetWidth;
      // and the hall ripples: each phone's edges flash once, a little later by seat, front to back
      aura.style.setProperty('--wave', `${((v.seat ?? 0) * 53) % 900}ms`);
      comps.classList.add('incoming'); aura.classList.add('ripple');
    }
    shownScene = idx;
  }
  [...comps.children].forEach((e, i) => (e as HTMLElement).style.setProperty('--i', String(i)));
  const b = v.ballot;
  if (b && b.id !== selBallot) {
    selBallot = b.id; ballotSeen = Date.now(); selection = { ...(b.myVote ?? {}) }; saved = !!b.myVote; dirty = false; editing = false;
    step = 0; turnout = null;
    if (!saved) announce(b.title, b.fields.length, b.closesAt, v.seat ?? 0);
  } else if (b && !dirty && b.myVote) { selection = { ...b.myVote }; saved = true; }
  // the ballot closed under this phone: say so once, then the scene's content comes back
  if (!b && hadBallot) { say(hadBallot.sealed ? 'Oylama kapandı · sonuç sahnede' : 'Oylama kapandı'); intro.classList.remove('on'); }
  hadBallot = b ? { id: b.id, sealed: saved } : null;
  drawBallot();
  // lantern and blackout take the whole screen; the content stays underneath and returns with the light
  cover.className = 'p-cover' + (v.mood === 'lantern' ? ' lantern' : v.mood === 'blackout' ? ' blackout' : '');
  cover.replaceChildren(...(v.mood === 'lantern' ? [h('div', 'p-cover-t', 'Telefonunu kaldır'), h('div', 'p-cover-s', 'Bu salonda yanan her ışık bir karar')] : v.mood === 'blackout' ? [h('div', 'p-cover-hush', 'sahneye bak')] : []));
  light(v);
  maybeHire(v);
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
function say(text: string): void {
  toast.textContent = text; toast.classList.add('on');
  clearTimeout(toastTimer); toastTimer = setTimeout(() => toast.classList.remove('on'), 2600);
}

/* ---------- onboarding: scanning the code is being hired ---------- */

function maybeHire(v: PhoneView): void {
  if (preview || !token) return;
  const k = `meridian-hired-${token}`;
  if (store(k)) return;
  store(k, '1');
  hire.replaceChildren();
  const box = h('div', 'hire-box');
  const steps = ['Kimlik doğrulanıyor', `Departman atanıyor: ${v.dept.name}`, `Personel numarası: ${v.memberId}`, 'Yetki kartı basılıyor'];
  box.append(h('div', 'hire-org', '◆ MERIDIAN · İNSAN VE KÜLTÜR'));
  const list = h('div', 'hire-steps');
  steps.forEach((t, i) => { const r = h('div', 'hire-step', t); r.style.animationDelay = `${300 + i * 520}ms`; list.appendChild(r); });
  const stamp = h('div', 'hire-stamp', 'İŞE ALINDIN');
  stamp.style.animationDelay = `${300 + steps.length * 520}ms`;
  box.append(list, stamp);
  hire.appendChild(box);
  hire.classList.add('on');
  buzz([10, 60, 10]);
  const end = 300 + steps.length * 520;
  setTimeout(() => buzz(40), end);
  setTimeout(() => { hire.classList.add('out'); }, end + 1500);
  // the badge comes out of the printer as the onboarding fades
  setTimeout(() => { hire.classList.remove('on', 'out'); hire.replaceChildren(); comps.classList.add('printed'); }, end + 2300);
}

/* ---------- the decision moment ---------- */

/**
 * A ballot opening takes the screen for a moment. Phones start a little apart by seat, so the hall lights up as
 * a wave rather than all at once; a tap skips it.
 */
let introTimer: ReturnType<typeof setTimeout> | undefined;
function announce(title: string, n: number, closesAt: string, seat: number): void {
  if (preview) return;
  const left = Math.max(0, Math.round((Date.parse(closesAt) - serverNow()) / 1000));
  const t = left >= 60 ? `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}` : `${left} sn`;
  clearTimeout(introTimer);
  intro.replaceChildren();
  const box = h('div', 'intro-box');
  box.append(h('div', 'intro-k', 'Gelen karar'), h('div', 'intro-t', 'KARAR ANI'), h('div', 'intro-s', title), h('div', 'intro-m', `${n} adım · ${t}`));
  intro.append(h('div', 'intro-scan'), box);
  const wave = (seat * 53) % 700;
  intro.onclick = () => { intro.classList.remove('on'); };
  introTimer = setTimeout(() => {
    intro.classList.add('on'); buzz([30, 50, 30]);
    introTimer = setTimeout(() => intro.classList.remove('on'), 2100);
  }, wave);
}

let ringTimer: ReturnType<typeof setInterval> | undefined;
let lastTick = -1;
function ring(closesAt: string): HTMLElement {
  const wrap = h('div', 'b-ring');
  const R = 26, C = 2 * Math.PI * R;
  wrap.innerHTML = `<svg viewBox="0 0 64 64"><circle cx="32" cy="32" r="${R}" class="ring-bg"/><circle cx="32" cy="32" r="${R}" class="ring-fg" stroke-dasharray="${C.toFixed(1)}"/></svg>`;
  const num = h('span', 'b-ring-n');
  wrap.appendChild(num);
  const fg = wrap.querySelector('.ring-fg') as SVGCircleElement;
  const end = Date.parse(closesAt); const total = Math.max(1000, end - Math.min(ballotSeen || serverNow(), serverNow()));
  const tick = () => {
    const left = Math.max(0, end - serverNow());
    const s = Math.round(left / 1000);
    num.textContent = s > 0 ? (s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : String(s)) : '0';
    fg.style.strokeDashoffset = String(C * (1 - left / total));
    const urgent = s > 0 && s <= 10;
    wrap.classList.toggle('urgent', urgent);
    // the last ten seconds, for a phone that has not sealed: the edges pulse and the phone ticks once a second
    const waiting = urgent && !(saved && !dirty);
    document.body.classList.toggle('urgent', waiting);
    if (waiting && s !== lastTick) { lastTick = s; buzz(12); }
  };
  clearInterval(ringTimer); ringTimer = setInterval(tick, 250); tick();
  return wrap;
}

const LETTERS = 'ABCDEFGH';
const pick = (f: BallotField) => f.options.findIndex((o) => o.id === selection[f.id]);

function drawBallot(): void {
  const b = view?.ballot;
  document.body.classList.toggle('has-ballot', !!b);
  if (!b) { clearInterval(ringTimer); document.body.classList.remove('urgent', 'sealed'); ballotBox.replaceChildren(); dock.replaceChildren(); return; }
  const isSealed = saved && !dirty && !editing;
  document.body.classList.toggle('sealed', isSealed);
  const frag = document.createDocumentFragment();
  const head = h('div', 'b-head');
  const titles = h('div', 'b-titles');
  titles.append(h('div', 'b-kicker', isSealed ? 'Kararın mühürlendi' : 'Karar anı'), h('h2', 'b-title', b.title));
  head.append(titles, ring(b.closesAt));
  frag.appendChild(head);

  if (isSealed) { frag.appendChild(receipt(b.id, b.fields)); ballotBox.replaceChildren(frag); dock.replaceChildren(); drawTurnout(); return; }

  const n = b.fields.length;
  if (step > n) step = n;
  if (n > 1) {
    const segs = h('div', 'b-steps');
    for (let i = 0; i <= n; i++) {
      const done = i < n ? !!selection[b.fields[i].id] : false;
      const seg = h('button', 'b-seg' + (i === step ? ' now' : '') + (done ? ' done' : ''), undefined, { type: 'button', 'aria-label': i < n ? b.fields[i].title : 'Özet' }) as HTMLButtonElement;
      seg.append(h('span', 'b-seg-n', i < n ? String(i + 1) : '✓'));
      const reach = i < n ? (i === 0 || !!selection[b.fields[i - 1].id]) : b.fields.every((f) => selection[f.id]);
      seg.disabled = !reach;
      seg.onclick = () => { step = i; drawBallot(); };
      segs.appendChild(seg);
    }
    frag.appendChild(segs);
  }

  const panel = h('div', 'b-panel');
  if (step < n) {
    const f = b.fields[step];
    panel.classList.add('step');
    panel.append(h('div', 'b-count', n > 1 ? `Adım ${step + 1} / ${n}` : 'Tek adım'), h('h3', 'b-q', f.title));
    const opts = h('div', 'b-opts', undefined, { role: 'radiogroup', 'aria-label': f.title });
    f.options.forEach((o, i) => {
      const on = selection[f.id] === o.id;
      const card = h('button', 'opt' + (on ? ' sel' : ''), undefined, { type: 'button', role: 'radio', 'aria-checked': String(on) });
      card.style.setProperty('--i', String(i));
      const txt = h('span', 'opt-body');
      txt.append(h('span', 'opt-text', o.text));
      if (o.hint) txt.append(h('span', 'opt-hint', o.hint));
      card.append(h('span', 'opt-mark', LETTERS[i] ?? '•'), txt, h('span', 'opt-lock'));
      card.onclick = () => {
        if (sending) return;
        selection[f.id] = o.id; dirty = true; saved = false; buzz(10);
        opts.querySelectorAll('.opt').forEach((e) => e.classList.toggle('recede', e !== card));
        opts.querySelectorAll('.opt').forEach((e) => e.classList.remove('sel'));
        card.classList.add('sel', 'locking');
        const at = step;
        setTimeout(() => { if (step === at) { step = at + 1; drawBallot(); } }, 480);
      };
      opts.appendChild(card);
    });
    panel.appendChild(opts);
  } else {
    panel.classList.add('review');
    panel.append(h('div', 'b-count', 'Kararın'));
    b.fields.forEach((f, i) => {
      const k = pick(f); const o = f.options[k];
      const row = h('button', 'rv-row', undefined, { type: 'button' });
      row.append(h('span', 'opt-mark', LETTERS[k] ?? '•'), h('span', 'rv-body', undefined));
      (row.lastChild as HTMLElement).append(h('span', 'rv-q', f.title), h('span', 'rv-a', o?.text ?? '—'));
      row.append(h('span', 'rv-edit', 'değiştir'));
      row.onclick = () => { step = i; drawBallot(); };
      panel.appendChild(row);
    });
  }
  frag.appendChild(panel);
  ballotBox.replaceChildren(frag);

  if (step >= n && b.fields.every((f) => selection[f.id])) dock.replaceChildren(sealPad());
  else dock.replaceChildren(h('div', 'b-hint', step < n ? 'Bir seçenek seç' : 'Eksik adım var'));
  drawTurnout();
}

/**
 * The seal: hold to commit. A tap only says how; holding fills the ring with a tick at each quarter, and the
 * vote goes out when it closes. Letting go early leaves everything as it was.
 */
const HOLD = 850;
function sealPad(): HTMLElement {
  const wrap = h('div', 'seal-wrap');
  const pad = h('button', 'seal' + (sending ? ' sending' : ''), undefined, { type: 'button', 'aria-label': 'Basılı tutarak mühürle' }) as HTMLButtonElement;
  const R = 46, C = 2 * Math.PI * R;
  pad.innerHTML = `<svg viewBox="0 0 100 100" aria-hidden="true"><circle cx="50" cy="50" r="${R}" class="seal-bg"/><circle cx="50" cy="50" r="${R}" class="seal-fg" stroke-dasharray="${C.toFixed(1)}" stroke-dashoffset="${C.toFixed(1)}"/>`
    + `<g class="seal-print"><path d="M50 26a24 24 0 0 1 24 24"/><path d="M50 32a18 18 0 0 1 18 18v6"/><path d="M50 38a12 12 0 0 1 12 12v10"/><path d="M38 50a12 12 0 0 1 6-10.4"/><path d="M32 50a18 18 0 0 1 9-15.6"/><path d="M26 50a24 24 0 0 1 12-20.8"/><path d="M50 44a6 6 0 0 1 6 6v14"/><path d="M44 50v8a6 6 0 0 0 1 3.4"/><path d="M38 56v2a12 12 0 0 0 3.5 8.5"/></g></svg>`;
  const label = h('div', 'seal-l', sending ? 'Mühürleniyor…' : 'Basılı tut · mühürle');
  wrap.append(pad, label);
  if (preview) { pad.disabled = true; return wrap; }
  const fg = pad.querySelector('.seal-fg') as SVGCircleElement;
  let t0 = 0; let raf = 0; let ticks = 0; let done: ReturnType<typeof setTimeout> | undefined;
  const reset = (hint: boolean) => {
    cancelAnimationFrame(raf); clearTimeout(done); t0 = 0; ticks = 0; pad.classList.remove('holding'); fg.style.strokeDashoffset = String(C);
    if (hint) { label.textContent = 'Biraz daha uzun basılı tut'; wrap.classList.remove('nudge'); void wrap.offsetWidth; wrap.classList.add('nudge'); }
  };
  const frame = () => {
    const k = Math.min(1, (performance.now() - t0) / HOLD);
    fg.style.strokeDashoffset = String(C * (1 - k));
    const q = Math.floor(k * 4); if (q > ticks) { ticks = q; buzz(6 + q * 4); }
    if (k >= 1) return;
    raf = requestAnimationFrame(frame);
  };
  // the ring is drawn per frame, but the commit is a timer: a phone that stops painting still seals on time
  const fire = () => { cancelAnimationFrame(raf); t0 = 0; fg.style.strokeDashoffset = '0'; commit(); };
  const start = (e: Event) => {
    e.preventDefault(); if (sending || t0) return;
    t0 = performance.now(); pad.classList.add('holding'); label.textContent = 'Bırakma…'; buzz(8); raf = requestAnimationFrame(frame); done = setTimeout(fire, HOLD);
  };
  const stop = () => { if (t0) reset(performance.now() - t0 < HOLD); };
  pad.addEventListener('pointerdown', (e) => { pad.setPointerCapture?.(e.pointerId); start(e); });
  pad.addEventListener('pointerup', stop); pad.addEventListener('pointercancel', () => reset(false));
  pad.addEventListener('contextmenu', (e) => e.preventDefault());
  pad.addEventListener('keydown', (e) => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) start(e); });
  pad.addEventListener('keyup', (e) => { if (e.key === ' ' || e.key === 'Enter') stop(); });
  return wrap;
}

function commit(): void {
  const b = view?.ballot; if (!b || sending) return;
  sending = true;
  client.send({ t: 'vote', ballot: b.id, vote: { ...selection }, ref: 'vote' });
  drawBallot();
}

/**
 * The sealed decision leaves for the stage: a point of light rises off the top of the screen, the phone flashes
 * once (in a dark hall, a seal is something the room can see) and then goes dark with the rest.
 */
function launch(): void {
  buzz([20, 40, 80]);
  if (preview) return;
  const from = dock.querySelector('.seal')?.getBoundingClientRect();
  const x = from ? from.left + from.width / 2 : innerWidth / 2; const y = from ? from.top + from.height / 2 : innerHeight * 0.8;
  const orb = h('div', 'fx-orb'); orb.style.left = `${x}px`; orb.style.top = `${y}px`;
  const wave = h('div', 'fx-wave'); wave.style.left = `${x}px`; wave.style.top = `${y}px`;
  fx.append(wave, orb);
  orb.animate([{ transform: 'translate(-50%, -50%) scale(1)', opacity: 1 }, { transform: 'translate(-50%, -50%) scale(.7)', opacity: 1, offset: 0.25 }, { transform: `translate(-50%, ${-y - 80}px) scale(.35)`, opacity: 0.9 }], { duration: 900, easing: 'cubic-bezier(.5, 0, .2, 1)', fill: 'forwards' }).onfinish = () => orb.remove();
  wave.animate([{ transform: 'translate(-50%, -50%) scale(.2)', opacity: 0.9 }, { transform: 'translate(-50%, -50%) scale(6)', opacity: 0 }], { duration: 800, easing: 'ease-out' }).onfinish = () => wave.remove();
  fx.classList.remove('flash'); void fx.offsetWidth; fx.classList.add('flash');
}

/** A short, stable code for the receipt, from the member and the decision; only for the eye. */
function sealCode(ballot: string): string {
  const s = `${token ?? ''}|${ballot}|${JSON.stringify(selection)}`; let x = 2166136261;
  for (let i = 0; i < s.length; i++) { x ^= s.charCodeAt(i); x = Math.imul(x, 16777619); }
  const hex = (x >>> 0).toString(16).toUpperCase().padStart(8, '0');
  return `${ballot} · ${hex.slice(0, 4)}-${hex.slice(4)}`;
}

function receipt(ballot: string, fields: BallotField[]): HTMLElement {
  const r = h('div', 'rc' + (Date.now() - sealedAt < 1500 ? ' fresh' : ''));
  const top = h('div', 'rc-top');
  top.append(h('span', 'rc-seal', '✓'), h('span', 'rc-t', 'Mühürlendi'), h('span', 'rc-code', sealCode(ballot)));
  r.appendChild(top);
  for (const f of fields) {
    const k = pick(f);
    const row = h('div', 'rc-row');
    row.append(h('span', 'opt-mark', LETTERS[k] ?? '•'), h('span', 'rc-a', f.options[k]?.text ?? '—'));
    r.appendChild(row);
  }
  r.append(h('div', 'rc-turnout'));
  const change = h('button', 'rc-change', 'Kararı değiştir', { type: 'button' });
  change.onclick = () => { editing = true; step = 0; drawBallot(); };
  r.append(h('div', 'rc-foot', 'Süre bitene kadar değiştirebilirsin. Gözün sahnede.'), change);
  return r;
}

/** How many in your group have sealed, as a field of lights that fill in; the room's total beneath. */
function drawTurnout(): void {
  const el = ballotBox.querySelector('.rc-turnout') as HTMLElement | null;
  const mini = dock.querySelector('.b-hint') as HTMLElement | null;
  const v = view; const t = turnout;
  if (!v || !t || t.ballot !== v.ballot?.id) { if (el) el.replaceChildren(); return; }
  const [done, asked] = t.byDept[v.dept.id] ?? [0, 0];
  if (mini) mini.dataset.turnout = `${v.dept.name}: ${done}/${asked} mühürledi`;
  if (!el) return;
  const dots = h('div', 'rc-dots' + (asked > 60 ? ' fine' : ''));
  const shown = Math.min(asked, 120); const lit = asked ? Math.round((done / asked) * shown) : 0;
  for (let i = 0; i < shown; i++) dots.appendChild(h('i', i < lit ? 'on' : ''));
  el.replaceChildren(h('div', 'rc-tt', `${v.dept.name} · ${done}/${asked} mühürledi`), dots, h('div', 'rc-room', `Salon: ${t.total[0]}/${t.total[1]}`));
}

/* ---------- the lamp ---------- */

let raf = 0;
function light(v: PhoneView): void {
  cancelAnimationFrame(raf);
  const s = document.body.style;
  if (!MOVING.includes(v.mood)) { s.removeProperty('--glow'); s.removeProperty('--fill'); s.removeProperty('--lamp'); return; }
  const o = { group: v.group ?? 0, color: v.dept.color, since: v.since ? Date.parse(v.since) : serverNow(), delay: ((v.seat ?? 0) * 137) % 4000 };
  const frame = () => {
    const l = lampAt(v.mood as Mood, serverNow(), o);
    s.setProperty('--glow', l.glow.toFixed(3)); s.setProperty('--fill', l.fill.toFixed(3)); s.setProperty('--lamp', l.color);
    raf = requestAnimationFrame(frame);
  };
  frame();
}

async function wake(): Promise<void> {
  try {
    const nav = navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<unknown> } };
    await nav.wakeLock?.request('screen');
  } catch { /* ignore */ }
}
void wake();
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void wake(); });
